/**
 * Instagram handle + permalink normalisation for the Creator picks panel
 * (campaign T57 WP0-B; contract `META_INSTAGRAM_CONTRACT.md` §1.1 and §1.2).
 *
 * Pure functions: no DOM, no network. WP0 makes no call to Instagram at all
 * (decision D-10) — everything here is string work on what John pasted.
 *
 * ── WHY `canonicalPostUrl` IS BYTE-PINNED TO THE SERVER ──────────────────────
 * `creator_recommendations.source_url` is normalised and validated by the
 * BEFORE trigger `trg_creator_recs_canonical_url`, and that trigger is the only
 * authority: it rewrites whatever we send. This module exists so John sees the
 * refusal *before* the round trip, and so the form can show him the canonical
 * form it is about to store.
 *
 * That makes one rule absolute (WP0A_HANDOVER §8.7): this must be **no more
 * permissive than the trigger**. Anything we accept that it rejects becomes a
 * raw Postgres `23514` in John's face; anything we reject that it accepts is
 * merely a slightly strict console. So the accept path below is a transcription
 * of the trigger's own algorithm, in its order:
 *
 *     btrim(source_url, E' \t\r\n')  ->  split on '#'  ->  split on '?'
 *     ^https?://(?:[A-Za-z0-9_-]+\.)*instagram\.com/(p|reel)/([A-Za-z0-9_-]{5,64})/?$   (case-insensitive)
 *     canonical := 'https://www.instagram.com/' || lower(kind) || '/' || code || '/'
 *
 * Two consequences that are easy to get wrong, both deliberate:
 *
 *  1. **`String.prototype.trim()` is NOT used.** JS `trim()` strips the whole
 *     Unicode whitespace set — NBSP, form feed, BOM, vertical tab. Postgres
 *     `btrim(x, E' \t\r\n')` strips exactly four characters. A permalink copied
 *     out of a web page routinely carries a leading U+00A0, and `trim()` would
 *     make the console accept a string the trigger then refuses. We strip the
 *     same four bytes the server strips, and no more.
 *  2. **Nothing may sit between the host and `/p/` or `/reel/`.** The trigger
 *     learned this the expensive way (its own comment block): an earlier regex
 *     allowed one leading segment so that `instagram.com/{user}/p/{code}/` would
 *     work, and that segment silently swallowed `/share/p/<token>` — a share
 *     REDIRECTOR id, not a shortcode — storing a permalink that 404s on a
 *     traveller's phone with no error anywhere in the chain. Deny-by-default,
 *     not a blocklist.
 *
 * The two refusals John will actually hit get their own message and hint, in the
 * server's own words, so the console and the database say the same thing.
 */

/** Instagram's documented username limit. Governs the handle regex below. */
const HANDLE_MAX = 30;

/**
 * `btrim(x, E' \t\r\n')` — exactly the four characters Postgres strips, in place
 * of JS `trim()`. See the header note: parity with the trigger is the point.
 */
const PG_BTRIM = /^[ \t\r\n]+|[ \t\r\n]+$/g;

/**
 * The trigger's regex, transcribed. `(?:[A-Za-z0-9_-]+\.)*instagram\.com` is a
 * host alternation that refuses `instagram.com@evil.example` ('@' outside the
 * charset), `evil.example/instagram.com` ('/' outside it) and
 * `www.instagram.com:443` (':' outside it) — each verified server-side by WP0-A.
 * The `{5,64}` floor is theirs too: every shortcode ever issued is ≥ 10
 * base64url characters, so `/p/x/` is junk, not a post.
 */
const POST_URL_RE =
  /^https?:\/\/(?:[A-Za-z0-9_-]+\.)*instagram\.com\/(p|reel)\/([A-Za-z0-9_-]{5,64})\/?$/i;

/** `https://www.instagram.com/share/p/<token>/` — the iOS share sheet's output. */
const SHARE_URL_RE = /^https?:\/\/(?:[A-Za-z0-9_-]+\.)*instagram\.com\/share\//i;

/** `https://www.instagram.com/{username}/p/{code}/` — the username-scoped form. */
const PREFIXED_POST_RE =
  /^https?:\/\/(?:[A-Za-z0-9_-]+\.)*instagram\.com\/[A-Za-z0-9._]+\/(p|reel)\//i;

/**
 * A profile URL with exactly ONE path segment: `https://instagram.com/<handle>/`.
 * Built from HANDLE_MAX so the cap lives in exactly one place — raising the
 * constant must not leave a second, stricter literal governing the real answer.
 */
const PROFILE_URL_RE = new RegExp(
  `^https?://(?:[A-Za-z0-9_-]+\\.)*instagram\\.com/@?([A-Za-z0-9._]{1,${HANDLE_MAX}})/?$`,
  'i',
);

const HANDLE_RE = new RegExp(`^[a-z0-9._]{1,${HANDLE_MAX}}$`);

/**
 * Instagram path segments that are not usernames.
 *
 * The real gate against a post URL being read as a handle is the ONE-SEGMENT
 * rule in `PROFILE_URL_RE` — `instagram.com/p/ABC12345678/` has two segments and
 * never reaches here. This list is the second net, for the degenerate
 * single-segment case (`instagram.com/p/`) and for someone typing a bare
 * reserved word into the handle box. It is a net, never the gate: a blocklist is
 * allow-by-default and Instagram can mint a reserved path any week.
 */
const RESERVED_SEGMENTS = new Set([
  'p', 'reel', 'reels', 'tv', 'stories', 'share', 'explore',
  'accounts', 'direct', 'about', 'developer', 'legal', 'privacy',
]);

/** Strip the fragment then the query, exactly as the trigger does. */
function stripFragmentAndQuery(value: string): string {
  const noFragment = value.split('#')[0] ?? '';
  return noFragment.split('?')[0] ?? '';
}

/**
 * Normalise an Instagram handle for storage — contract §1.2, both directions.
 *
 * Accepts `@Handle`, ` handle `, `HANDLE`, and a pasted profile URL
 * (`https://www.instagram.com/handle/`, with or without `www`, `@`, a trailing
 * slash or an `?hl=en` tail). Returns the handle **without** `@` and lowercased,
 * which is what the column stores and what `query_creator_recs_for_provider`
 * returns; or `null` when the input is not a handle at all.
 *
 * Returning `null` rather than a best guess is deliberate: a wrong handle is a
 * dead `@name` on a traveller's screen, and the operator is right there to fix
 * it if we say so.
 */
export function normaliseHandle(raw: string | null | undefined): string | null {
  if (raw === null || raw === undefined) return null;

  // Contract §1.2 "strip whitespace" — all of it, not just the ends: a handle
  // has no legal internal space, so `@florence foodie` is a paste artefact.
  const collapsed = raw.replace(/\s+/g, '');
  if (collapsed === '') return null;

  let candidate = collapsed;

  // A pasted profile URL reduces to its single path segment. Query and fragment
  // go first so `https://www.instagram.com/foodie/?hl=en` works — that tail is
  // on every profile link Instagram's own share button produces.
  if (/^https?:\/\//i.test(candidate)) {
    const profile = PROFILE_URL_RE.exec(stripFragmentAndQuery(candidate));
    if (!profile) return null;          // a post URL, or some other instagram path
    candidate = profile[1] ?? '';
  }

  candidate = candidate.replace(/^@/, '').toLowerCase();

  if (!HANDLE_RE.test(candidate)) return null;   // HANDLE_RE already caps the length
  if (RESERVED_SEGMENTS.has(candidate)) return null;
  // All-dots is not a username; it is what an empty paste leaves behind.
  if (!/[a-z0-9_]/.test(candidate)) return null;

  return candidate;
}

/** Contract §1.2: stored without `@`, displayed with it. */
export function displayHandle(handle: string | null | undefined): string | null {
  const h = normaliseHandle(handle);
  return h === null ? null : `@${h}`;
}

/** What `canonicalPostUrl` answers. `ok: false` carries the server's own words. */
export type CanonicalPostUrl =
  | { ok: true; url: string; kind: 'p' | 'reel'; shortcode: string }
  | { ok: false; reason: string; hint: string };

/**
 * Canonicalise an Instagram post permalink — contract §1.1, mirroring
 * `trg_creator_recs_canonical_url`.
 *
 * On success the `url` is byte-identical to what the trigger would store, so the
 * form can show John the exact string that is about to be written (and so the
 * `uq_creator_recs_post_venue` "already paired" check lines up client-side).
 *
 * The refusal messages and hints are copied from the trigger's `RAISE` blocks so
 * that a rejection here reads the same as a rejection there — one vocabulary,
 * whichever layer catches it.
 */
export function canonicalPostUrl(raw: string | null | undefined): CanonicalPostUrl {
  const cleaned = stripFragmentAndQuery(String(raw ?? '').replace(PG_BTRIM, ''));

  const match = POST_URL_RE.exec(cleaned);
  if (match) {
    // The `i` flag necessarily lets `/P/` and `/REEL/` match, which is why the
    // canonical form is rebuilt from a lowercased kind. The SHORTCODE is
    // base64url and case-sensitive, so it is kept exactly as captured.
    const kind = (match[1] ?? '').toLowerCase() as 'p' | 'reel';
    const shortcode = match[2] ?? '';
    return { ok: true, url: `https://www.instagram.com/${kind}/${shortcode}/`, kind, shortcode };
  }

  // An invisible character is the one refusal the operator cannot diagnose by
  // looking. If the string becomes a valid permalink once the FULL Unicode
  // whitespace set is stripped, then the URL is right and the paste is dirty —
  // a leading U+00A0 off a web page, a BOM out of a spreadsheet cell. Say that,
  // instead of "this is not an Instagram post permalink", which sends John
  // hunting for a typo that is not there.
  //
  // This is a MESSAGE branch only. It still refuses, so the one-way rule holds:
  // Postgres btrim strips four bytes and would refuse this too. Widening the
  // trim to match JS here is what would break parity.
  if (POST_URL_RE.test(stripFragmentAndQuery(String(raw ?? '').trim()))) {
    return {
      ok: false,
      reason: 'That permalink has an invisible character in it (a non-breaking space or '
        + 'similar), so the database would refuse it.',
      hint: 'Delete the whole field and retype or re-paste the URL — copying it from the '
        + 'browser address bar rather than from a page or a chat message usually fixes it.',
    };
  }

  if (SHARE_URL_RE.test(cleaned)) {
    return {
      ok: false,
      reason: 'That is an Instagram SHARE link, not a permalink.',
      hint: 'The id in /share/p/… is a redirector token, not the post shortcode, and it would be '
        + 'stored as a dead link. Open the post and copy the URL from the address bar instead — '
        + 'it looks like https://www.instagram.com/p/C9xYz12AbCd/',
    };
  }

  if (PREFIXED_POST_RE.test(cleaned)) {
    return {
      ok: false,
      reason: 'That link has a path segment before /p/ or /reel/.',
      hint: 'Only the bare permalink is accepted, so that one post has exactly one stored '
        + 'spelling. Drop everything between instagram.com/ and /p/ — '
        + 'https://www.instagram.com/p/C9xYz12AbCd/',
    };
  }

  return {
    ok: false,
    reason: 'That is not an Instagram post permalink (expected '
      + 'https://www.instagram.com/p/<shortcode>/ or /reel/<shortcode>/).',
    hint: 'Stories, IGTV (/tv/) and profile URLs are not posts and cannot be paired.',
  };
}
